import axios, { type InternalAxiosRequestConfig } from 'axios'
import { useToast } from '@/composables/useToast'

const APP_ACCESS_DENIED_MESSAGE = 'Für diesen Bereich haben Sie keine Berechtigung.'

function resolveApiBaseUrl() {
  const configuredBaseUrl = new URLSearchParams(window.location.search).get('apiBaseUrl')?.trim()
  if (configuredBaseUrl) {
    return configuredBaseUrl
  }

  return '/api'
}

function isEmbeddedAppRuntime() {
  return window.location.protocol === 'file:' || window.location.host === 'appassets.androidplatform.net'
}

function redirectToLogin() {
  if (isEmbeddedAppRuntime()) {
    window.location.hash = '#/login'
    return
  }

  window.location.href = '/login'
}

function redirectToDashboard() {
  if (isEmbeddedAppRuntime()) {
    window.location.hash = '#/dashboard'
    return
  }

  window.location.href = '/dashboard'
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/ld+json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function processQueue(error: unknown, token: string | null) {
  for (const { resolve, reject } of refreshQueue) {
    if (token) {
      resolve(token)
    } else {
      reject(error)
    }
  }
  refreshQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      _retryAppAccess?: boolean
    }

    const isAppAccessDenied =
      error.response?.status === 403 && error.response?.data?.detail === APP_ACCESS_DENIED_MESSAGE

    if (isAppAccessDenied) {
      if (!originalRequest._retryAppAccess && originalRequest.url !== '/auth/refresh') {
        originalRequest._retryAppAccess = true
        try {
          const { data } = await api.post('/auth/refresh')
          const newToken = data.token as string
          localStorage.setItem('jwt_token', newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return await api(originalRequest)
        } catch {
          // Refresh selbst fehlgeschlagen – unten mit Toast + Redirect abschliessen
        }
      }

      useToast().error(APP_ACCESS_DENIED_MESSAGE)
      redirectToDashboard()
      return Promise.reject(error)
    }

    // Don't retry refresh/login requests or already-retried requests
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url === '/auth/refresh' ||
      originalRequest.url === '/auth/login'
    ) {
      if (error.response?.status === 401) {
        localStorage.removeItem('jwt_token')
        redirectToLogin()
      }
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Queue this request until the refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(api(originalRequest))
          },
          reject,
        })
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const { data } = await api.post('/auth/refresh')
      const newToken = data.token as string
      localStorage.setItem('jwt_token', newToken)
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      processQueue(null, newToken)
      return api(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      localStorage.removeItem('jwt_token')
      redirectToLogin()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const message = err.response?.data?.detail ?? err.response?.data?.message
    if (typeof message === 'string' && message.trim() !== '') {
      return message
    }
  }

  return fallback
}

export default api
